/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from "react";
import ct from "class-types.macro";
import { z } from "zod";
import { SubmitHandler } from "react-hook-form";
import formatAsCurrency from "@wss/utilities/formatAsCurrency";
import Form from "~/common/Form";
import SelectInput from "~/common/Form/SelectInput";
import Input from "~/common/Form/Input";

const schema = z.object({
  fryType: z.string().nonempty("What are you frying is required"),
  fryerSize: z.coerce
    .number()
    .min(1, "How large is your fryer(s) be a number greater than 0."),
  howManyFryers: z.coerce
    .number()
    .min(1, "How many friers must be a number greater than 0."),
  howManyFryingDays: z.coerce
    .number()
    .min(1, "How many frying days per week must be a number greater than 0.")
    .max(7, "How many frying days per week must be a number less than 8."),
  oilCost: z.coerce
    .number()
    .gt(0, "Cost per lb. of oil must be a number greater than 0."),
});

interface Props {
  price: number;
}

type FormFields = z.infer<typeof schema>;

const fryingFoodOptions = [
  {
    text: "French Fries and Other Non-Breaded Foods",
    value: "1",
    withoutFilteringOilReplacementTimeText: "3 1/2 Days",
    withoutFilteringOilReplacementTimeValue: 3.5,
    withFilteringOilReplacementTimeText: "7 Days",
    withFilteringOilReplacementTimeValue: 7,
  },
  {
    text: "Breaded or Crumbly Foods",
    value: "2",
    withoutFilteringOilReplacementTimeText: "2 Days",
    withoutFilteringOilReplacementTimeValue: 2,
    withFilteringOilReplacementTimeText: "4 Days",
    withFilteringOilReplacementTimeValue: 4,
  },
  {
    text: "Breaded Fish",
    value: "3",
    withoutFilteringOilReplacementTimeText: "2 1/2 Days",
    withoutFilteringOilReplacementTimeValue: 2.5,
    withFilteringOilReplacementTimeText: "5 Days",
    withFilteringOilReplacementTimeValue: 5,
  },
  {
    text: "Non-Breaded Vegetables",
    value: "4",
    withoutFilteringOilReplacementTimeText: "4 Days",
    withoutFilteringOilReplacementTimeValue: 4,
    withFilteringOilReplacementTimeText: "8 Days",
    withFilteringOilReplacementTimeValue: 8,
  },
  {
    text: "Non-Breaded Meats & Poultry",
    value: "5",
    withoutFilteringOilReplacementTimeText: "2 Days",
    withoutFilteringOilReplacementTimeValue: 2,
    withFilteringOilReplacementTimeText: "4 Days",
    withFilteringOilReplacementTimeValue: 4,
  },
];

const FrycloneFilterMachineCalculator = ({ price }: Props) => {
  const [
    withoutFilteringOilReplacementTime,
    setWithoutFilteringOilReplacementTime,
  ] = useState("");
  const [withFilteringOilReplacementTime, setWithFilteringOilReplacementTime] =
    useState("");
  const [savingsPerMonth, setSavingsPerMonth] = useState("");
  const [payoff, setPayoff] = useState("");

  const handleReset = (event: React.SyntheticEvent) => {
    event.preventDefault();
    setWithoutFilteringOilReplacementTime("");
    setWithFilteringOilReplacementTime("");
    setSavingsPerMonth("");
    setPayoff("");
  };

  const handleSubmit: SubmitHandler<FormFields> = (data) => {
    const selectedFryType = fryingFoodOptions.find(
      (option) => option.value === data.fryType,
    );

    const fryerCountNumber = Number(data.howManyFryers);
    const fryingDaysPerWeekNumber = Number(data.howManyFryingDays);
    const costPerLbOfOilNumber = Number(data.oilCost);
    const perBatchOilCost =
      Number(data.fryerSize) * costPerLbOfOilNumber * fryerCountNumber;
    const monthlyFryingDays = fryingDaysPerWeekNumber * 4;
    const numberOfBatchesWithoutFiltering =
      monthlyFryingDays /
      (selectedFryType?.withoutFilteringOilReplacementTimeValue || 1);
    const numberOfBatchesWithFiltering =
      monthlyFryingDays /
      (selectedFryType?.withFilteringOilReplacementTimeValue || 1);
    const savingsPerMonth =
      numberOfBatchesWithoutFiltering * perBatchOilCost -
      numberOfBatchesWithFiltering * perBatchOilCost;
    const payoffInMonths = price / savingsPerMonth;

    setWithoutFilteringOilReplacementTime(
      selectedFryType?.withoutFilteringOilReplacementTimeText || "",
    );
    setWithFilteringOilReplacementTime(
      selectedFryType?.withFilteringOilReplacementTimeText || "",
    );
    setSavingsPerMonth(
      `${formatAsCurrency.formatIfUndefined(savingsPerMonth)}`,
    );
    setPayoff(`${payoffInMonths.toFixed(1)} Months`);
  };

  const outputStyles: React.CSSProperties = {
    borderRadius: "4px",
    boxShadow: "inset 0 1px 4px rgb(0 0 0 / 4%), 0 1px 0 #fff",
    border: "1px solid rgba(0,0,0,.12)",
    background: "rgba(0,0,0,.04)",
  };

  return (
    <div
      style={{
        borderRadius: "4px",
        boxShadow: "0 1px 4px rgb(0 0 0 / 8%), 0 1px 0 white inset",
      }}
      className={ct(
        "bg-gray-200",
        "border-gray-200",
        "border-solid",
        "border",
        "leading-6",
        "mx-auto",
        "mt-5",
        "mb-10",
        "text-left",
        "w-4/5",
        "xxl:w-11/12",
      )}
    >
      <div
        className={ct(
          "border-gray-200",
          "border-solid",
          "border",
          "border-t-0",
          "border-r-0",
          "border-l-0",
          "border-b",
          "py-3",
          "px-5",
        )}
      >
        <h4
          style={{ textShadow: "0 1px 0 #fff" }}
          className={ct("text-xl", "leading-6", "m-0")}
        >
          Generated Savings Calculator
        </h4>
      </div>
      <Form<FormFields> validationSchema={schema} onSubmit={handleSubmit}>
        <div
          style={{ textShadow: "0 1px 0 white" }}
          className={ct("text-xl", "px-5", "py-4-1/2")}
        >
          <div data-testid="input-container">
            <SelectInput
              name="fryType"
              options={fryingFoodOptions}
              label="What are you frying?"
              placeholder="Select an option"
              labelClassName={ct("font-semibold", "mb-2", "text-gray-800")}
              data-testid="frying-food-option-select"
            />
            <Input
              type="text"
              label="How many fryers?"
              placeholder="Enter number of fryers"
              name="howManyFryers"
              labelClassName={ct("font-semibold", "mb-2", "text-gray-800")}
              data-testid="fryer-count-input"
              defaultValues=""
            />
            <Input
              type="text"
              label="How large is your fryer(s)?"
              placeholder="Enter size of fryer(s)"
              name="fryerSize"
              labelClassName={ct("font-semibold", "mb-2", "text-gray-800")}
              data-testid="fryer-size-input"
            />

            <Input
              type="text"
              label="How many frying days per week?"
              placeholder="Enter number of frying days per week"
              name="howManyFryingDays"
              labelClassName={ct("font-semibold", "mb-2", "text-gray-800")}
              data-testid="frying-days-input"
              defaultValues="1"
            />

            <Input
              type="text"
              label="Cost per lb. of oil"
              name="oilCost"
              labelClassName={ct("font-semibold", "mb-2", "text-gray-800")}
              data-testid="fryer-oil-cost-input"
              defaultValues="0.55"
            />
          </div>
          <hr className={ct("mt-4", "mb-8")} />

          <div style={outputStyles} className={ct("py-3", "px-4")}>
            <div
              style={{ borderBottom: "1px solid rgba(0,0,0,.08)" }}
              className={ct(
                "font-semibold",
                "text-base",
                "mb-1",
                "pb-1",
                "text-gray-800",
              )}
            >
              Without filtering, oil needs to be replaced in
            </div>
            <div
              data-testid="fryer-without-filtering-output"
              className={ct("font-semibold", "text-2xl", "text-gray-900")}
            >
              {withoutFilteringOilReplacementTime}
            </div>
          </div>

          <div style={outputStyles} className={ct("mt-8", "py-3", "px-4")}>
            <div
              style={{ borderBottom: "1px solid rgba(0,0,0,.08)" }}
              className={ct(
                "font-semibold",
                "text-base",
                "mb-1",
                "pb-1",
                "text-gray-800",
              )}
            >
              With daily filtering, oil needs to be replaced in
            </div>
            <div
              data-testid="fryer-with-filtering-output"
              className={ct("font-semibold", "text-2xl", "text-gray-900")}
            >
              {withFilteringOilReplacementTime}
            </div>
          </div>

          <div style={outputStyles} className={ct("mt-8", "py-3", "px-4")}>
            <div
              style={{ borderBottom: "1px solid rgba(0,0,0,.08)" }}
              className={ct(
                "font-semibold",
                "text-base",
                "mb-1",
                "pb-1",
                "text-gray-800",
              )}
            >
              Monthly Savings
            </div>
            <div
              data-testid="fryer-monthly-savings-output"
              className={ct("font-semibold", "text-2xl", "text-gray-900")}
            >
              {savingsPerMonth}
            </div>
          </div>

          <div style={outputStyles} className={ct("mt-8", "py-3", "px-4")}>
            <div
              style={{ borderBottom: "1px solid rgba(0,0,0,.08)" }}
              className={ct(
                "font-semibold",
                "text-base",
                "mb-1",
                "pb-1",
                "text-gray-800",
              )}
            >
              Payoff
            </div>
            <div
              data-testid="fryer-payoff-output"
              className={ct("font-semibold", "text-2xl", "text-gray-900")}
            >
              {payoff}
            </div>
          </div>
        </div>

        <div
          className={ct(
            "border-gray-200",
            "border-solid",
            "border",
            "border-r-0",
            "border-b-0",
            "border-l-0",
            "border-t",
            "px-5",
            "py-2-1/2",
          )}
        >
          <button
            type="submit"
            className="btn btn-primary"
            data-testid="fryer-calculate-button"
          >
            Calculate
          </button>
          <a
            href=""
            className={ct("float-right", "text-base", "py-2")}
            style={{ textShadow: "0 1px 0 #fff" }}
            onClick={handleReset}
            data-testid="fryer-reset-button"
          >
            Reset
          </a>
        </div>
      </Form>
    </div>
  );
};

export default FrycloneFilterMachineCalculator;
